<template>
  <v-container fluid>
    <v-card class="pa-4">
      <!-- Header con botón Nuevo Volumen -->
      <div class="d-flex align-center mb-4">
        <v-icon color="#ce501b" size="large" class="mr-2">mdi-harddisk</v-icon>
        <h2 class="text-h5">Gestión de Volúmenes</h2>
        <v-spacer></v-spacer>
        <v-btn
          color="#ce501b"
          prepend-icon="mdi-plus"
          @click="abrirModalNuevoVolumen"
          elevation="2"
        >
          Nuevo Volumen
        </v-btn>
        <v-btn
          icon
          @click="recargarDesdeInicio"
          :loading="loading && volumenes.length === 0"
          color="#ffe8e1"
          class="ml-2"
        >
          <v-icon color="#ce501b">mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Buscador -->
      <v-text-field
        ref="campoBusquedaRef"
        v-model="busqueda"
        prepend-inner-icon="mdi-magnify"
        label="Buscar volumen..."
        variant="outlined"
        density="comfortable"
        clearable
        class="mb-4"
        @input="filtrarVolumenes"
      ></v-text-field>

      <!-- Tabla con infinite scroll -->
      <div 
        class="volumenes-container" 
        @scroll="handleScroll"
        ref="scrollContainer"
      >
        <!-- Tabla -->
        <v-table class="volumenes-table" fixed-header>
          <thead>
            <tr>
              <th class="text-center">VOLID</th>
              <th>Path</th>
              <th>Path Texto</th>
              <th class="text-center">Estado</th>
              <th class="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(volumen, index) in volumenesFiltrados"
              :key="index"
              class="volumen-row"
            >
              <!-- VOLID -->
              <td class="text-center">
                <v-chip color="primary" size="small" label>
                  {{ volumen.volId }}
                </v-chip>
              </td>

              <!-- Path -->
              <td>
                <div class="d-flex align-center">
                  <v-icon class="mr-2" size="small" color="blue-grey">mdi-folder</v-icon>
                  <span class="text-caption">{{ volumen.path }}</span>
                </div>
              </td>

              <!-- Path Texto -->
              <td>
                <span class="text-caption">{{ volumen.pathTexto }}</span>
              </td>

              <!-- Estado -->
              <td class="text-center">
                <v-chip 
                  :color="volumen.estado === 1 ? '' : 'grey'" 
                  :style="volumen.estado === 1 ? 'background-color: #ffe8e1; color: #ce501b' : ''"
                  size="small"
                  label
                >
                  {{ volumen.estado === 1 ? 'Activo' : 'Inactivo' }}
                </v-chip>
              </td>

              <!-- Acciones -->
              <td class="text-center">
                <v-btn
                  icon
                  size="small"
                  color="orange"
                  variant="text"
                  @click="editarVolumen(volumen)"
                  class="mr-1"
                >
                  <v-icon size="small" color="grey">mdi-pencil</v-icon>
                  <v-tooltip activator="parent" location="top">Editar</v-tooltip>
                </v-btn>

                <v-btn
                  v-if="volumen.estado === 0"
                  icon
                  size="small"
                  color="red"
                  variant="text"
                  @click="confirmarEliminar(volumen)"
                >
                  <v-icon size="small">mdi-delete</v-icon>
                  <v-tooltip activator="parent" location="top">Eliminar</v-tooltip>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Indicador de carga al final -->
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular
            indeterminate
            color="primary"
            size="50"
          ></v-progress-circular>
          <p class="mt-2 text-caption">Cargando más volúmenes...</p>
        </div>

        <!-- Mensaje cuando no hay más datos -->
        <div v-if="!loading && !hayMasDatos && volumenes.length > 0" class="text-center py-4 text-caption text-grey">
          <v-icon>mdi-check-circle</v-icon>
          No hay más volúmenes para mostrar
        </div>

        <!-- Mensaje cuando no hay datos -->
        <div v-if="!loading && volumenes.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey">mdi-harddisk-remove</v-icon>
          <p class="text-h6 text-grey mt-2">No hay volúmenes registrados</p>
        </div>
      </div>
    </v-card>

    <!-- Modal Nuevo/Editar Volumen -->
    <v-dialog v-model="dialogNuevoVolumen" max-width="600px" persistent :retain-focus="false">
      <v-card>
        <v-card-title class="text-h6 text-white" style="background-color: #004d82;">
          {{ modoEdicion ? 'Editar Volumen' : 'Nuevo Volumen' }}
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="cerrarModal"
            size="small"
            variant="text"
            color="white"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="formNuevoVolumen" v-model="validFormNuevo">
            <!-- Path -->
            <v-text-field
              ref="campoPathRef"
              v-model="nuevoVolumen.path"
              label="Path"
              prepend-icon="mdi-folder"
              variant="outlined"
              density="comfortable"
              :rules="[v => !!v || 'Path es requerido']"
              class="mb-3 campo-amarillo"
              hint="Ejemplo: D:\proyecto_2026\software_antiguo\contenido_multimedia_imagenes\Imagenes"
              persistent-hint
            ></v-text-field>

            <!-- Path Texto -->
            <v-text-field
              v-model="nuevoVolumen.pathTexto"
              label="Path Texto"
              prepend-icon="mdi-text"
              variant="outlined"
              density="comfortable"
              :rules="[v => !!v || 'Path Texto es requerido']"
              class="mb-3 campo-amarillo"
              hint="Ejemplo: D:\proyecto_2026\software_antiguo\contenido_multimedia_imagenes\Imagenes"
              persistent-hint
            ></v-text-field>

            <!-- Estado -->
            <v-select
              v-model="nuevoVolumen.estado"
              :items="estadosDisponibles"
              item-title="text"
              item-value="value"
              label="Estado"
              prepend-icon="mdi-toggle-switch"
              variant="outlined"
              density="comfortable"
              :rules="[v => [0, 1].includes(v) || 'Estado es requerido']"
              class="mb-3"
            ></v-select>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="cerrarModal"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            @click="guardarVolumen"
            :loading="guardando"
            :disabled="!validFormNuevo"
          >
            {{ modoEdicion ? 'Actualizar' : 'Grabar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import axios from '@/plugins/axios'
import Swal from 'sweetalert2'

const volumenes = ref([])
const loading = ref(false)
const hayMasDatos = ref(true)
const paginaActual = ref(1)
const itemsPorPagina = ref(20)
const scrollContainer = ref(null)
const busqueda = ref('')
const dialogNuevoVolumen = ref(false)
const todosLosVolumenes = ref([])
const guardando = ref(false)
const validFormNuevo = ref(false)
const formNuevoVolumen = ref(null)
const modoEdicion = ref(false)
const volumenEditando = ref(null)

const campoBusquedaRef = ref(null)
const campoPathRef = ref(null)

// Estados disponibles
const estadosDisponibles = [
  { text: 'Seleccionar este volumen como predeterminado', value: 1 },
  { text: 'No seleccionar este volumen como predeterminado', value: 0 }
]

// Datos del nuevo volumen
const nuevoVolumen = ref({
  path: '',
  pathTexto: '',
  estado: 1
})

onMounted(async () => {
  await cargarVolumenes()
  document.addEventListener('keydown', handleKeyboardGlobal)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyboardGlobal)
})


const cargarVolumenes = async () => {
  if (loading.value || !hayMasDatos.value) return
  
  loading.value = true
  
  try {
    if (todosLosVolumenes.value.length === 0) {
      // TODO: Reemplazar con endpoint real
      const response = await axios.get('/api/Volumenes/list-volumenes')
      
      if (response.data.success) {
        todosLosVolumenes.value = response.data.data
       //console.log('Volúmenes cargados:', todosLosVolumenes.value.length)         //Usarlo solo para ver los errores en el testeo de Software
      } else {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: response.data.mensaje || 'No se pudieron cargar los volúmenes',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
        return
      }
    }
    
    const inicio = (paginaActual.value - 1) * itemsPorPagina.value
    const fin = inicio + itemsPorPagina.value
    const itemsPaginados = todosLosVolumenes.value.slice(inicio, fin)
    
    if (itemsPaginados.length > 0) {
      volumenes.value = [...volumenes.value, ...itemsPaginados]
      paginaActual.value++
    }
    
    if (fin >= todosLosVolumenes.value.length) {
      hayMasDatos.value = false
    }
    
  } catch (error) {
    //console.error('Error al cargar volúmenes:', error)         //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar los volúmenes',
      customClass: {
        title: 'swal-title-custom',
        container: 'swal-sobre-modal'
      }
    })
  } finally {
    loading.value = false
  }
}

const handleScroll = (event) => {
  const { scrollTop, scrollHeight, clientHeight } = event.target
  if (scrollTop + clientHeight >= scrollHeight * 0.8) {
    cargarVolumenes()
  }
}

const recargarDesdeInicio = () => {
  volumenes.value = []
  todosLosVolumenes.value = []
  paginaActual.value = 1
  hayMasDatos.value = true
  cargarVolumenes()
}

const volumenesFiltrados = computed(() => {
  if (!busqueda.value) {
    return volumenes.value
  }
  
  const termino = busqueda.value.toLowerCase()
  return volumenes.value.filter(volumen => 
    volumen.volId?.toString().includes(termino) ||
    volumen.path?.toLowerCase().includes(termino) ||
    volumen.pathTexto?.toLowerCase().includes(termino)
  )
})

const filtrarVolumenes = () => {
  // Filtrado automático con computed
}

const abrirModalNuevoVolumen = () => {
  modoEdicion.value = false
  limpiarFormulario()
  dialogNuevoVolumen.value = true
   nextTick(() => {
    setTimeout(() => {
      const input = campoPathRef.value?.$el?.querySelector('input')
      if (input) input.focus()
    }, 100)
  })
  
}


const handleKeyboardGlobal = (event) => {
  if (Swal.isVisible()) return;   // ← AGREGAR ESTA LÍNEA al inicio
  // Alt + N → abrir modal Nuevo Volumen
  if (event.altKey && event.code === 'KeyN') {
    event.preventDefault()
    if (!dialogNuevoVolumen.value) {
      abrirModalNuevoVolumen()
    }
  }

  // Alt + B → enfocar buscador
  if (event.altKey && event.code === 'KeyB') {
    event.preventDefault()
    if (!dialogNuevoVolumen.value) {
      const input = campoBusquedaRef.value?.$el?.querySelector('input')
      if (input) input.focus()
    }
  }

  //Alt + G → guardar/actualizar volumen
  if (event.altKey && event.code === 'KeyG') {
    event.preventDefault()
    if (dialogNuevoVolumen.value) {
      guardarVolumen()
    }
  }

  // ESC → cerrar modal
  if (event.key === 'Escape') {
    if (dialogNuevoVolumen.value) {
      event.preventDefault()
      cerrarModal()
    }
  }
}










const editarVolumen = async (volumen) => {
  //console.log('Editar volumen:', volumen)       //Usarlo solo para ver los errores en el testeo de Software
  modoEdicion.value = true
  volumenEditando.value = volumen
  
  try {
    // TODO: Cargar detalle del volumen desde el API
    const response = await axios.get(`/api/Volumenes/detalle/${volumen.volId}`)
    
    if (response.data.success) {
      const detalle = response.data.data
      //console.log('Detalle del volumen:', detalle)           //Usarlo solo para ver los errores en el testeo de Software
      
      nuevoVolumen.value = {
        path: detalle.path,
        pathTexto: detalle.pathTexto,
        estado: detalle.estado
      }
      
      dialogNuevoVolumen.value = true
    } else {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: response.data.mensaje || 'No se pudo cargar el volumen',
         customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        },
      })
    }
  } catch (error) {
    //console.error('Error al cargar detalle del volumen:', error)      //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: 'Hubo un problema al cargar el volumen',
      customClass: {
       title: 'swal-title-custom',
       container: 'swal-sobre-modal'
      }
    })
  }
}

const guardarVolumen = async () => {
  const { valid } = await formNuevoVolumen.value.validate()
  
  if (!valid) {
    return
  }

  // ✅ NUEVO: Auto-agregar backslash al final de pathTexto si no lo tiene
  if (nuevoVolumen.value.pathTexto && !nuevoVolumen.value.pathTexto.endsWith('\\')) {
    nuevoVolumen.value.pathTexto = nuevoVolumen.value.pathTexto + '\\'
  }

  if (nuevoVolumen.value.path && !nuevoVolumen.value.path.endsWith('\\')) {
    nuevoVolumen.value.path = nuevoVolumen.value.path + '\\'
  }

  guardando.value = true

  try {
    const idUsuarioSesion = localStorage.getItem('idUsuario')
    
    if (!idUsuarioSesion) {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'No se pudo obtener la información del usuario en sesión',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
      guardando.value = false
      return
    }

    if (modoEdicion.value) {
      // ========================================
      // MODO EDICIÓN - Actualizar volumen
      // ========================================
      const payload = {
        volId: volumenEditando.value.volId,
        path: nuevoVolumen.value.path,
        pathTexto: nuevoVolumen.value.pathTexto,
        estado: nuevoVolumen.value.estado
      }

      //console.log('Actualizando volumen:', payload)       //Usarlo solo para ver los errores en el testeo de Software

      const response = await axios.put(
        `/api/Volumenes/actualizar-volumen?idUsuarioSesion=${idUsuarioSesion}`,
        payload
      )

      if (response.data.success) {
        cerrarModal()
        
        await Swal.fire({
          icon: 'success',
          iconColor: '#ce501b',
          title: '¡Éxito!',
          text: 'Volumen actualizado correctamente',
          timer: 2500,
          confirmButtonColor: '#ce501b',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal',
            confirmButton: 'swal-btn-confirmar'
          },
          showConfirmButton: true
        })

        recargarDesdeInicio()
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: response.data.mensaje || 'No se pudo actualizar el volumen',
        })
      }
    } else {
      // ========================================
      // MODO NUEVO - Crear volumen
      // ========================================
      const payload = {
        path: nuevoVolumen.value.path,
        pathTexto: nuevoVolumen.value.pathTexto,
        estado: nuevoVolumen.value.estado
      }

      //console.log('Creando volumen:', payload)       //Usarlo solo para ver los errores en el testeo de Software

      const response = await axios.post(
        `/api/Volumenes/registrar-volumen?idUsuarioSesion=${idUsuarioSesion}`,
        payload
      )

      //console.log('Respuesta recibida:', response.data)    //Usarlo solo para ver los errores en el testeo de Software

      if (response.data.success) {
        cerrarModal()
        
        await Swal.fire({
          icon: 'success',
          iconColor: '#ce501b',
          title: '¡Éxito!',
          text: 'Volumen registrado correctamente',
          timer: 2500,
          confirmButtonColor: '#ce501b',
          customClass: {
           title: 'swal-title-custom',
           container: 'swal-sobre-modal',
           confirmButton: 'swal-btn-confirmar',
          },
          showConfirmButton: true
        })

        recargarDesdeInicio()
      } else {
        Swal.fire({
          icon: 'error',
          iconColor: '#ce501b',
          title: 'Error',
          text: response.data.mensaje || 'No se pudo registrar el volumen',
          customClass: {
            title: 'swal-title-custom',
            container: 'swal-sobre-modal'
          }
        })
      }
    }
  } catch (error) {
    //console.error('Error al guardar volumen:', error)    //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      text: error.response?.data?.mensaje || 'Hubo un problema al guardar el volumen',
      confirmButtonColor: '#ce501b',
      allowEnterKey: true,              // ← AGREGAR
      focusConfirm: true,               // ← AGREGAR
      keydownListenerCapture: true,     // ← AGREGAR
      customClass: {
       title: 'swal-title-custom',
       container: 'swal-sobre-modal'
      },
        didOpen: () => {                  // ← AGREGAR
        Swal.getConfirmButton()?.focus()
      }
    })
  } finally {
    guardando.value = false
  }
}

const limpiarFormulario = () => {
  nuevoVolumen.value = {
    path: '',
    pathTexto: '',
    estado: 1
  }
  if (formNuevoVolumen.value) {
    formNuevoVolumen.value.reset()
  }
}

const cerrarModal = () => {
  dialogNuevoVolumen.value = false
  modoEdicion.value = false
  volumenEditando.value = null
  limpiarFormulario()
}

const confirmarEliminar = async (volumen) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Deseas eliminar el volumen "${volumen.path}"?`,
    icon: 'warning',
    iconColor: '#ce501b',
    showCancelButton: true,
    confirmButtonColor: '#ce501b',
    cancelButtonColor: '#9B9B9B',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
    customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal',
          confirmButton: 'swal-btn-confirmar',
          cancelButton: 'swal-btn-cancelar'
    },
  })

  if (result.isConfirmed) {
    eliminarVolumen(volumen)
  }
}

const eliminarVolumen = async (volumen) => {
  //console.log('Eliminar volumen:', volumen)    //Usarlo solo para ver los errores en el testeo de Software
  
  try {
    const idUsuarioSesion = localStorage.getItem('idUsuario')
    
    if (!idUsuarioSesion) {
      Swal.fire({
        icon: 'error',
        iconColor: '#ce501b',
        title: 'Error',
        text: 'No se pudo obtener la información del usuario en sesión',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
      return
    }

    const response = await axios.delete(
      `/api/Volumenes/eliminar-volumen/${volumen.volId}?idUsuarioSesion=${idUsuarioSesion}`
    )

    //console.log('Respuesta eliminación:', response.data)  //Usarlo solo para ver los errores en el testeo de Software

    if (response.data.success) {
      await Swal.fire({
        icon: 'success',
        iconColor: '#ce501b',
        title: '¡Eliminado!',
        text: `El volumen ha sido eliminado exitosamente.`,
        timer: 2500,
        confirmButtonColor: '#ce501b',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal',
          confirmButton: 'swal-btn-confirmar'
        },
        showConfirmButton: true
      })

      recargarDesdeInicio()
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        iconColor: '#ce501b',
        text: response.data.mensaje || 'No se pudo eliminar el volumen',
        customClass: {
          title: 'swal-title-custom',
          container: 'swal-sobre-modal'
        }
      })
    }
  } catch (error) {
    //console.error('Error al eliminar volumen:', error)              //Usarlo solo para ver los errores en el testeo de Software
    Swal.fire({
      icon: 'error',
      iconColor: '#ce501b',
      title: 'Error',
      customClass: {
        title: 'swal-title-custom',
        container: 'swal-sobre-modal'
      },
      text: error.response?.data?.mensaje || 'Hubo un problema al eliminar el volumen'
    })
  }
}


</script>

<style scoped>
.volumenes-container {
  max-height: calc(100vh - 320px);
  overflow-y: auto;
  padding-right: 8px;
}

.volumenes-container::-webkit-scrollbar {
  width: 8px;
}

.volumenes-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.volumenes-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.volumenes-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.volumenes-table {
  background-color: transparent;
}

.volumenes-table th {
  background-color: #def2ff !important;
  font-weight: bold !important;
  color: #004d82 !important;
  white-space: nowrap;
}

.volumen-row {
  transition: background-color 0.2s ease;
}

.volumen-row:hover {
  background-color: rgba(59, 130, 200, 0.05);
}

.volumenes-table td {
  padding: 12px 16px !important;
}

/* Campos amarillos */
:deep(.campo-amarillo .v-field) {
  background-color: #FFFACD !important;
}
</style>


<style>
.swal-sobre-modal {
  z-index: 10000 !important;
}
</style>

<style>

.swal-title-custom {
  color: #004d82 !important;
  font-size: 1.3rem !important;
}

.swal-text-custom {
  color: #555555 !important;
  font-size: 0.95rem !important;
}

.swal-btn-custom {
  background-color: #ce501b !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  padding: 8px 24px !important;
}

.swal-btn-custom:hover {
  background-color: #a83d14 !important;
}

.swal-btn-confirmar {
  color: white !important;
}

</style>